/* eslint-disable complexity */
import clsx from 'clsx';
import React, {
  ReactNode,
  RefObject,
  TextareaHTMLAttributes,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { useRows } from '~/components/chat';
import { isAppBasePath } from '~/routes/app-paths';
import { Statistics } from '~/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';
import { useDocumentSearch } from '~/views/alfred/document-search/use-document-search/useDocumentSearch';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';

import { ChatInputContextHeader } from '../chat-input-context-header/ChatInputContextHeader';
import { AnalysisModeDropdown } from './AnalysisModeDropdown';
import styles from './ChatDocumentContextInput.module.scss';
import { WebSearchDropdown } from './web-search/WebSearchDropdown';

export interface ChatDocumentContextInputProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onKeyDown'> {
  children?: ReactNode;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  isMessagePending: boolean;
  contextCounts?: {
    documentsCount: number;
    subTypeCount: number;
    statistics?: Statistics;
  };
  onKeyDownProp?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  textAreaWrapperClassName?: string;
}

const useFocusOnNavigation = ({
  inputRef,
  location,
  disabled,
}: {
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  location: string;
  disabled?: boolean;
}) => {
  useLayoutEffect(() => {
    if (disabled) {
      return;
    }

    const focusInput = () => inputRef?.current?.focus();
    const refocusIfNothingFocused = () => {
      if (document.activeElement === document.body) {
        focusInput();
      }
    };

    focusInput();
    window.addEventListener('focus', refocusIfNothingFocused);

    return () => {
      window.removeEventListener('focus', refocusIfNothingFocused);
    };
  }, [location, disabled, inputRef]);
};

export const ChatDocumentContextInput = ({
  children,
  value,
  isMessagePending,
  inputRef,
  contextCounts,
  autoFocus,
  placeholder,
  disabled,
  onChange,
  onKeyDownProp,
  textAreaWrapperClassName,
}: ChatDocumentContextInputProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { rows, updateRows } = useRows({
    value: value,
    textAreaRef: inputRef,
  });

  const { mode } = useChatNavigation();
  const location = useLocation();
  const isAlfredPage = isAppBasePath(location.pathname);
  const hasActiveDocumentMessage = useDocumentSearch(
    state => !state.isMessagePending && !!state.messages.length
  );

  const [showTools, setShowTools] = useState(isAlfredPage ? true : false);

  useFocusOnNavigation({
    inputRef,
    location: `${location.pathname}${location.search}`,
    disabled,
  });

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateRows(event);
    onChange?.(event);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.stopPropagation();
    } else {
      onKeyDownProp?.(e);
    }
  };

  const showToolsOnNonAlfredPage = !isAlfredPage && showTools;
  const showToolsOnAlfredPage = isAlfredPage && mode === 'research';

  const showChildrenInTextArea =
    (isAlfredPage && mode !== 'research') || (!isAlfredPage && !showTools);
  const showChildrenInTools =
    !isAlfredPage || (isAlfredPage && mode === 'research');

  const wrapperClassName = isMessagePending
    ? styles.chatInputFieldWrapperPending
    : styles.chatInputFieldWrapper;

  return (
    <div className={wrapperClassName}>
      <div className={styles.chatInputFieldContainer} ref={wrapperRef}>
        {mode === 'document' && isAlfredPage && hasActiveDocumentMessage && (
          <ChatInputContextHeader contextCounts={contextCounts} />
        )}
        <div className={clsx(styles.textAreaWrapper, textAreaWrapperClassName)}>
          <textarea
            id="dropdown-trigger"
            className={styles.textareaElement}
            ref={inputRef}
            onChange={handleChange}
            rows={rows}
            value={value}
            autoFocus={autoFocus}
            placeholder={placeholder}
            aria-label={placeholder}
            disabled={disabled}
            onFocus={() => {
              setShowTools(true);
            }}
            data-testid="chat-input"
            {...(mode === 'research'
              ? { onKeyDown }
              : { onKeyDown: onKeyDownProp })}
          />
          {showChildrenInTextArea && children}
        </div>
        {(showToolsOnNonAlfredPage || showToolsOnAlfredPage) && (
          <div className={styles.alfredTools}>
            <div
              className={styles.toolsWrapper}
              onClick={e => {
                e.preventDefault();
              }}
            >
              <div className={styles.leftTools}>
                {mode === 'research' && (
                  <div className={styles.researchDropdowns}>
                    <AnalysisModeDropdown
                      contentRef={wrapperRef}
                      isMessagePending={isMessagePending}
                    />
                    <div className={styles.researchDropdownsDivider}>
                      <WebSearchDropdown />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {showChildrenInTools && children}
          </div>
        )}
      </div>
    </div>
  );
};
